import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import React from 'react';
import { Navigate } from 'react-router';
import VerifyPostModal from './VerifyPostModal';
import Page from '../app/layout/Page';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import QuillContext from '../editor/QuillContext';
import ResponsiveDate from './history/ResponsiveDate';
import useHistoryRange from './history/useHistoryRange';
import VersionChanges from './history/VersionChanges';
import VersionChangesWall from './history/VersionChangesWall';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import InternalTopbar from '../topbar/InternalTopbar';
import { UIActions } from '../ui/uiSlice';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import postPath from '../utils/url/postPath';

const POST_VERIFY_VIEW_QUERY = gql(/** @complexity 6 */ `
  query PostVerifyView($postId: ID!) {
    post(id: $postId) {
      id
      title
      content(isLatest: true) {
        id
        version
      }
      verification {
        id
      }
      verificationEntries {
        id
        version
        verifiedAt
      }
      ...VerifyPostModal_post
    }
    entityAssetToken(entityId: $postId, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface PostVerifyViewInnerProps {
  versionFrom: number;
  versionTo: number;
  post: DocumentType<typeof POST_VERIFY_VIEW_QUERY>['post'];
}

const PostVerifyViewInner = ({
  versionFrom,
  versionTo,
  post,
}: PostVerifyViewInnerProps) => {
  const verificationEntry = _.first(post?.verificationEntries);

  const { content, allVersionChanges } = useHistoryRange({
    postId: post.id,
    versionFrom,
    versionTo,
  });

  if (
    (allVersionChanges && allVersionChanges.length === 0) ||
    versionFrom === versionTo
  ) {
    return (
      <Navigate
        to={postPath({ id: post.id, title: '' })}
        replace
        state={{ verification: true }}
      />
    );
  }

  return (
    <VersionChanges.Root allVersionChanges={allVersionChanges}>
      <VersionChanges.Wrapper
        initialTitle="Verify Post - Slab"
        postTitle={post.title}
      >
        <VersionChanges.ContentGrid>
          <VersionChanges.TopbarContainer>
            <VersionChanges.Topbar>
              <VersionChanges.Meta>
                {verificationEntry && (
                  <ResponsiveDate
                    date={verificationEntry.verifiedAt}
                    render={({ condensed, dateWithPreposition }) => {
                      return condensed
                        ? `Verified ${dateWithPreposition}`
                        : `Post last verified ${dateWithPreposition}`;
                    }}
                  />
                )}
              </VersionChanges.Meta>
            </VersionChanges.Topbar>
          </VersionChanges.TopbarContainer>
          <VersionChanges.ScrollContainer>
            <VersionChanges.ContentContainer>
              <VersionChanges.Content>
                <VersionChanges.Post content={content} />
              </VersionChanges.Content>
              <VersionChangesWall
                versionFrom={versionFrom}
                versionTo={versionTo}
                postId={post.id}
              />
            </VersionChanges.ContentContainer>
          </VersionChanges.ScrollContainer>
        </VersionChanges.ContentGrid>
      </VersionChanges.Wrapper>
    </VersionChanges.Root>
  );
};

const PostVerifyView = () => {
  const [postId] = useSlug('postSlug');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data, loading } = useQuery(POST_VERIFY_VIEW_QUERY, {
    variables: { postId },
    fetchPolicy: 'no-cache',
  });

  if (loading) return null;

  const post = data?.post;
  const currentVerificationEntry = _.first(post?.verificationEntries);
  const verification = post?.verification;

  // The post will show the state bar if the user has been requested as a verifier
  if (
    post == null ||
    currentVerificationEntry == null ||
    verification == null
  ) {
    return (
      <Navigate
        to={postPath({ id: postId, title: '' })}
        replace
        state={{ verification: true }}
      />
    );
  }

  return (
    <Page
      topbar={
        <InternalTopbar
          postActions={
            <>
              <Button
                onClick={() =>
                  dispatch(
                    UIActions.openModal(VerifyPostModal, {
                      post,
                      onVerify: () => {
                        navigate(postPath(post));
                      },
                      close() {
                        dispatch(UIActions.closeModal());
                      },
                    }),
                  )
                }
                variant="primary"
              >
                Reverify
              </Button>
              <Button variant="secondary" to={postPath(post)}>
                Done
              </Button>
            </>
          }
        />
      }
    >
      <QuillContext>
        <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
          <PostVerifyViewInner
            versionFrom={currentVerificationEntry.version}
            versionTo={post.content.version}
            post={post}
          />
        </AssetTokenProvider>
      </QuillContext>
    </Page>
  );
};

export default PostVerifyView;
